import React, {useEffect, useMemo} from 'react';
import {MemberInfoBox} from '../MemberInfoBox/MemberInfoBox';
import {ExpressCheckoutButtons} from '@wix/ecom-platform-express-checkout-buttons/widget';
import {useControllerProps} from '../Widget/ControllerContext';
import {StepsManager} from '../CheckoutSteps/StepsManager/StepsManager';
import {CustomerDetailsStep} from '../CheckoutSteps/Steps/CustomerDetailsStep/CustomerDetailsStep';
import {StepId} from '../../../types/checkoutApp.types';
import {ErrorDialog} from '../ErrorDialog/ErrorDialog';
import {DeliveryMethodStep} from '../CheckoutSteps/Steps/DeliveryMethodStep/DeliveryMethodStep';
import {Violations} from '../Violations/Violations';
import {PaymentAndPlaceOrderStep} from '../CheckoutSteps/Steps/PaymentAndPlaceOrderStep/PaymentAndPlaceOrderStep';
import {classes} from './FullFlow.st.css';
import {classes as commonClasses} from '../../../common/common.st.css';
import {CheckoutSlot} from '../CheckoutSlot';
import {SlotId} from '../../../domain/utils/slotId';
import {trackInitiateCheckout} from '../../../domain/utils/analytics.utils';
import {MemberLoginStep} from '../CheckoutSteps/Steps/MemberLoginStep/MemberLoginStep';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../common/constants';
import {AdditionalDetailsStep} from '../CheckoutSteps/Steps/AdditionalDetailsStep/AdditionalDetailsStep';

export enum FullFlowDataHook {
  root = 'FullFlow.root',
  title = 'FullFlow.title',
  expressCheckout = 'FullFlow.expressCheckout',
  violations = 'FullFlow.violations',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FullFlow = () => {
  const {
    checkoutStore: {modalError, cartSummary, cart, shouldShowViolations, isShippingFlow, defaultViolationsToDisplay},
    stepsManagerStore: {shouldDisplayExpressCheckout, stepsList, activeStep},
    memberStore: {isMemberAreaAppInstalled},
    paymentStore: {isExpressEmpty},
    navigationStore: {trackEvent, isPreviewMode},
    isOneColumnView,
  } = useControllerProps();

  const {experiments} = useExperiments();
  const checkoutUIRevampOrderSummary = experiments.enabled(SPECS.CheckoutUIRevampOrderSummary);

  const isOnPlaceOrderStep = activeStep.stepId === StepId.paymentAndPlaceOrder;
  const shouldShowViolationsTopBanner = shouldShowViolations && !isOnPlaceOrderStep;

  const expressCheckoutClassName = useMemo(() => {
    if (isExpressEmpty) {
      return '';
    }
    if (checkoutUIRevampOrderSummary) {
      return isOneColumnView ? classes.expressCheckoutOneColumn : classes.expressCheckoutTwoColumn;
    }
    return classes.expressCheckoutOld;
  }, [isExpressEmpty, checkoutUIRevampOrderSummary, isOneColumnView]);

  useEffect(
    () => {
      trackEvent(...trackInitiateCheckout({cart}));
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ []
  );

  const fullFlowContent = () => (
    <>
      <div className={commonClasses.oneColumnViewPadding}>
        {shouldShowViolationsTopBanner && (
          <Violations
            dataHook={FullFlowDataHook.violations}
            isTopBanner={true}
            violations={defaultViolationsToDisplay}
          />
        )}
        <CheckoutSlot className={classes.TopCheckoutSlot} slotId={SlotId.TopCheckoutSlot} />
        {shouldDisplayExpressCheckout && (
          <div data-hook={FullFlowDataHook.expressCheckout} className={expressCheckoutClassName}>
            <ExpressCheckoutButtons
              currency={cart.paymentInfo.currencyCode}
              layout="horizontal"
              isMobile={isOneColumnView}
              withLoader={experiments.enabled(SPECS.ExpressLoaderInCheckout)}
              isShippable={isShippingFlow}
              estimatedTotal={cartSummary ? `${cartSummary.paymentSummary.payNow.amount}` : undefined}
              canStartPayment={!isPreviewMode}
            />
          </div>
        )}
        {isMemberAreaAppInstalled && <MemberInfoBox />}
        <CheckoutSlot
          className={
            checkoutUIRevampOrderSummary ? classes.BeforeStepsCheckoutSlot : classes.BeforeStepsCheckoutSlotOld
          }
          slotId={SlotId.BeforeStepsCheckoutSlot}
        />
      </div>
      <StepsManager>
        {stepsList.map((step) => {
          switch (step.id) {
            case StepId.memberLogin:
              return <MemberLoginStep key={StepId.memberLogin} />;
            case StepId.customerDetails:
              return <CustomerDetailsStep key={StepId.customerDetails} />;
            case StepId.additionalDetails:
              return <AdditionalDetailsStep key={StepId.additionalDetails} />;
            case StepId.deliveryMethod:
              return <DeliveryMethodStep key={StepId.deliveryMethod} />;
            case StepId.paymentAndPlaceOrder:
              return <PaymentAndPlaceOrderStep key={StepId.paymentAndPlaceOrder} />;
            /* istanbul ignore next */
            default:
              return null;
          }
        })}
      </StepsManager>
      {(cart.noItemsError || modalError) && <ErrorDialog />}
    </>
  );

  return <div data-hook={FullFlowDataHook.root}>{fullFlowContent()}</div>;
};
